import React, { useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import ContentEditable from "react-contenteditable"
import sanitizeHtml from "sanitize-html"
import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPenToSquare,
  faCheck,
  faTimes,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { rotate } from "./animations"

const Action = styled.div`
  flex: none;
  display: flex;
  width: 1.5rem;
`
const Text = styled.div`
  flex: auto;
  display: flex;
  align-items: stretch;
  > * {
    flex: auto;
  }
  padding-right: 0.5rem;
`
const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border: 1px dashed transparent;
  border-radius: 2px;
  &:hover {
    border-color: ${(props) =>
      !props.editing ? props.theme.colours.active : "transparent"};
  }
`

const Icon = styled(FontAwesomeIcon)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colours.text.tertiary};
`
const OKIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colours.activenew};
`
const CancelAlertIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colours.error};
`
const BusyIcon = styled(Icon)`
  animation: ${rotate} 2s linear infinite;
`

const UserEditable = ({ tagName = "div", value, onChange, disabled }) => {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const text = useRef(value)
  const innerRef = useRef()

  const handleChange = (evt) => {
    text.current = evt.target.value
  }

  const handleBlur = async () => {}

  const containerClicked = () => {
    // Set to edit mode
    !editing ? setEditing(true) : undefined
    /* To pre-select on click:
    innerRef.current.onfocus = () => {
      window.setTimeout(() => {
        if (window.getSelection && document.createRange) {
          const range = document.createRange()
          range.selectNodeContents(innerRef.current)
          const sel = window.getSelection()
          sel.removeAllRanges()
          sel.addRange(range)
        } else if (document.body.createTextRange) {
          const range = document.body.createTextRange()
          range.moveToElementText(innerRef.current)
          range.select()
        }
      }, 1)
    }
    */
    window.setTimeout(() => {
      innerRef.current.focus()
    }, 1)
  }

  useEffect(() => {
    if (text) {
      text.current = value
    }
    if (innerRef) {
      innerRef.current.innerText = value
    }
  }, [value])

  const onCancel = () => {
    setEditing(false)
    text.current = value // Reset text
  }

  const onSave = async () => {
    const v = sanitizeHtml(text.current, {
      allowedTags: [],
      allowedAttributes: {},
    })
    setEditing(false)
    setFailed(false)
    if (onChange) {
      setBusy(true)
      try {
        await onChange(v)
      } catch (failed) {
        setEditing(true)
        setFailed(failed)
      }
      setBusy(false)
    }
  }

  return (
    <Container
      title="Click to edit"
      onClick={containerClicked}
      editing={editing}
    >
      <Text>
        <ContentEditable
          innerRef={innerRef}
          tagName={tagName}
          html={text.current || ""}
          onBlur={handleBlur}
          onChange={handleChange}
          disabled={disabled === true ? true : !editing}
          onKeyDown={(e) => {
            // CTRL+Enter
            if (e.keyCode == 13 && e.ctrlKey) {
              onSave()
              return
            }
            // Escape
            if (e.key == "Escape") {
              onCancel()
              return
            }
          }}
        />
      </Text>
      <Action>
        {!busy && editing && (
          <CancelAlertIcon icon={faTimes} onClick={onCancel} />
        )}
      </Action>
      <Action>
        {!busy && !editing && (
          <Icon icon={faPenToSquare} onClick={containerClicked} />
        )}
        {!busy && editing && <OKIcon icon={faCheck} onClick={onSave} />}
        {busy && <BusyIcon icon={faSpinner} />}
        {failed && <CancelAlertIcon icon={faTriangleExclamation} />}
      </Action>
    </Container>
  )
}

UserEditable.propTypes = {
  tagName: PropTypes.string, // e.g. "p", "h1"
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool, // If provided, overrides editable status
}

export default UserEditable
